import { copyText } from './clipboard'

/**
 * ONE DECISION ABOUT HANDING OUT A LINK, for the same reason copyText exists.
 *
 * `navigator.share` is the platform's own door — on the phone it raises the iOS
 * share sheet, where Messages, Mail and "Copy" are the person's choice rather
 * than ours. It is ABSENT in most desktop browsers, so every caller needs a
 * fallback, and a caller that invents its own gets a button that silently does
 * nothing on half the machines that press it.
 *
 * TRANSIENT USER ACTIVATION is the trap here, exactly as in saveFile.ts: the
 * share sheet may only be raised while the tap's activation is live, and any
 * `await` between the tap and the call spends it — WebKit then answers
 * NotAllowedError, which presents as a dead button. So this function does NO
 * awaiting before `share()`, and callers must invoke it straight from the
 * handler.
 *
 * A DISMISSED SHEET IS NOT A FAILURE. WebKit rejects with AbortError when the
 * person closes the sheet, and reporting that as an error would tell them
 * something went wrong when they simply changed their mind.
 */
export type ShareOutcome = 'shared' | 'dismissed' | 'copied' | 'failed'

type ShareNavigator = Navigator & {
  share?: (data: { url: string; title?: string }) => Promise<void>
}

/** Whether this browser can raise a share sheet at all — for labelling the control. */
export function canShareLink(): boolean {
  return typeof (navigator as ShareNavigator).share === 'function'
}

/**
 * Offer `url` through the platform's share sheet, falling back to the clipboard.
 *
 * Call it directly from a click handler: nothing here may be awaited first.
 */
export function shareLink(url: string, title?: string): Promise<ShareOutcome> {
  const nav = navigator as ShareNavigator
  if (typeof nav.share !== 'function') {
    return copyText(url).then((r) => (r.ok ? 'copied' : 'failed'))
  }
  let p: Promise<void>
  try {
    // Synchronous throw is a real shape: a WebView with the API present and the
    // capability withheld throws rather than rejecting.
    p = nav.share({ url, ...(title ? { title } : {}) })
  } catch {
    return copyText(url).then((r) => (r.ok ? 'copied' : 'failed'))
  }
  return p.then(
    () => 'shared' as const,
    (e: unknown) => {
      if (e instanceof Error && e.name === 'AbortError') return 'dismissed' as const
      /*
       * NotAllowedError means the activation was already spent — falling back to
       * the clipboard turns a dead button into a working one rather than
       * reporting a failure the person cannot act on.
       */
      return copyText(url).then((r) => (r.ok ? ('copied' as const) : ('failed' as const)))
    },
  )
}
